import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WorkOrder from "../../models/workOrder";
import formHelper from "../../util/formHelper";
import Select from "react-select";
import SelectOption from "../../components/common/selectedOption";
import Location from "../../models/location";
import Account from "../../models/account";
import SubscriptionContextModel from "../../contexts/subscriptionContextModel";
import SubscriptionContext from "../../contexts/subscriptionContext";
import accountService from "../../services/accountService";
import Trade from "../../models/trade";
import tradeService from "../../services/tradeService";
import WorkOrderType from "../../models/workOrderType";
import workOrderTypeHelper from "../../models/workOrderTypeHelper";
import DatePicker from "react-datepicker";
import WorkOrderPriority from "../../models/workOrderPriority";
import workOrderPriorityHelper from "../../models/workOrderPriorityHelper";
import workOrderService from "../../services/workOrderService";
import Page from "../../components/work/layout/Page";

const WorkOrderEdit: React.FC = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();

  const { subscription } =
    useContext<SubscriptionContextModel>(SubscriptionContext);

  const [isNew, setIsNew] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [workOrder, setWorkOrder] = useState<WorkOrder>(new WorkOrder());

  const [accountOptions, setAccountOptions] = useState<SelectOption<Account>[]>(
    [],
  );

  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [locationsOptions, setLocationsOptions] = useState<
    SelectOption<Location>[]
  >([]);
  const [selectedLocationOption, setSelectedLocationOption] =
    useState<SelectOption<Location> | null>();

  const [tradeOptions, setTradeOptions] = useState<SelectOption<Trade>[]>([]);
  const [selectedTradeOption, setSelectedTradeOption] =
    useState<SelectOption<Trade> | null>(null);

  const [workOrderTypeOptions, setWorkOrderTypeOptions] = useState<
    SelectOption<WorkOrderType>[]
  >([]);
  const [selectedWorkOrderTypeOption, setSelectedWorkOrderTypeOption] =
    useState<SelectOption<WorkOrderType> | null>(null);

  const [workOrderPriorityOptions, setWorkOrderPriorityOptions] = useState<
    SelectOption<WorkOrderPriority>[]
  >([]);
  const [selectedWorkOrderPriotityOption, setSelectedWorkOrderPriotityOption] =
    useState<SelectOption<WorkOrderPriority> | null>(null);

  useEffect(() => {
    const init = async (isNew: boolean) => {
      const { data: accounts } = await accountService.getAccounts(
        subscription.idSubscription,
      );

      const accountOptions: SelectOption<Account>[] = [];

      for (const account of accounts) {
        accountOptions.push({
          value: account,
          label: account.accountName,
        });
      }

      setAccountOptions(accountOptions);

      const { data: locations } = await accountService.getLocations(
        subscription.idSubscription,
      );
      setAllLocations(locations);

      const tradeOptions: SelectOption<Trade>[] = [];

      const { data: trades } = await tradeService.getTrades();

      for (const trade of trades) {
        tradeOptions.push({
          value: trade,
          label: trade.tradeName,
        });
      }

      setTradeOptions(tradeOptions);

      // warn: do not add workOrder as dependency to this useEffect() as it will cause a lot of db calls that are super slow - this will affect create new work order if you add dependency - do edit work differently

      // const selectedTradeOption =
      //   tradeOptions.find((t) => t.value.idTrade === workOrder.idTrade) ?? null;
      // setSelectedTradeOption(selectedTradeOption);

      const workOrderTypeOptions: SelectOption<WorkOrderType>[] = [];

      for (const member in WorkOrderType) {
        const enumMember = member as WorkOrderType;

        if (enumMember === WorkOrderType.None) {
          continue;
        }
        workOrderTypeOptions.push({
          value: enumMember,
          label: workOrderTypeHelper.getDisplayValue(enumMember),
        });
      }

      setWorkOrderTypeOptions(workOrderTypeOptions);

      // const selectedWorkWorderTypeOption =
      //   workOrderTypeOptions.find(
      //     (wot) => wot.value === workOrder.workOrderType,
      //   ) ?? null;
      // setSelectedWorkOrderTypeOption(selectedWorkWorderTypeOption);

      const workOrderPriorityOptions: SelectOption<WorkOrderPriority>[] = [];

      for (const member in WorkOrderPriority) {
        const enumMember = member as WorkOrderPriority;

        if (enumMember === WorkOrderPriority.None) {
          continue;
        }
        workOrderPriorityOptions.push({
          value: enumMember,
          label: workOrderPriorityHelper.getDisplayValue(enumMember),
        });
      }

      setWorkOrderPriorityOptions(workOrderPriorityOptions);

      // const workOrderPriorityOption =
      //   workOrderPriorityOptions.find(
      //     (p) => p.value === workOrder.workOrderPriority,
      //   ) ?? null;
      // setSelectedWorkOrderPriotityOption(workOrderPriorityOption);
    };

    const isNew = idParam === "new";
    setIsNew(isNew);

    init(isNew);
  }, [idParam, subscription]);

  const onAccountChanged = (event: any) => {
    const { value: account }: SelectOption<Account> = event;

    const newWorkOrder = {
      ...workOrder,
      idAccount: account.idAccount,
      account: account,
    };
    setWorkOrder(newWorkOrder);

    setSelectedLocationOption(null);

    const filteredLocations = allLocations.filter(
      (l) => l.idAccount === account.idAccount,
    );

    if (filteredLocations) {
      const locationsOptions: SelectOption<Location>[] = [];

      for (const l of filteredLocations) {
        locationsOptions.push({
          value: l,
          label: l.locationName,
        });
      }

      setLocationsOptions(locationsOptions);
    }
  };

  const onLocationChanged = (event: any) => {
    const selectOption: SelectOption<Location> = event;
    setSelectedLocationOption(selectOption);

    const { value: location } = selectOption;

    const newWorkOrder = {
      ...workOrder,
      idLocation: location.idLocation,
      location: location,
    };
    setWorkOrder(newWorkOrder);
  };

  const onTradeChanged = (event: any) => {
    const tradeOption: SelectOption<Trade> = event;
    setSelectedTradeOption(tradeOption);

    const { value: trade } = tradeOption;

    const newWorkOrder = {
      ...workOrder,
      idTrade: trade.idTrade,
      trade: trade,
    };
    setWorkOrder(newWorkOrder);
  };

  const onWorkOrderTypeChanged = (event: any) => {
    const workOrderTypeOption: SelectOption<WorkOrderType> = event;
    setSelectedWorkOrderTypeOption(workOrderTypeOption);

    const { value: workOrderType } = workOrderTypeOption;

    const newWorkOrder = {
      ...workOrder,
      workOrderType: workOrderType,
    };
    setWorkOrder(newWorkOrder);
  };

  const onDneChanged = (event: any) => {
    const newValue = event.target.value;
    const dne = Number(newValue);
    const newWorkOrder = {
      ...workOrder,
      dneAmount: dne,
    };
    setWorkOrder(newWorkOrder);
  };

  const onDueDateChanged = (date: Date) => {
    const newWorkOrder = {
      ...workOrder,
      dueDate: date,
    };
    setWorkOrder(newWorkOrder);
  };

  const onWorkOrderPriorityChanged = (event: any) => {
    const workOrderPriorityOption: SelectOption<WorkOrderPriority> = event;
    setSelectedWorkOrderPriotityOption(workOrderPriorityOption);

    const { value: workOrderPriority } = workOrderPriorityOption;

    const newWorkOrder = {
      ...workOrder,
      workOrderPriority: workOrderPriority,
    };
    setWorkOrder(newWorkOrder);
  };

  const onEstimatedLaborHoursChanged = (event: any) => {
    const newValue = event.target.value;

    if (!newValue) {
      return;
    }

    const estimatedLaborHours = Number(newValue);
    const newWorkOrder = {
      ...workOrder,
      estimatedLaborHours: estimatedLaborHours,
    };
    setWorkOrder(newWorkOrder);
  };

  const onTagsChanged = (event: any) => {
    const newValue = event.target.value;

    const newWorkOrder = {
      ...workOrder,
      tags: newValue,
    };
    setWorkOrder(newWorkOrder);
  };

  const onReferenceNumberChanged = (event: any) => {
    const newValue = event.target.value;

    const newWorkOrder = {
      ...workOrder,
      referenceNumber: newValue,
    };
    setWorkOrder(newWorkOrder);
  };

  const onScopeOfWorkChanged = (event: any) => {
    const newValue = event.target.value;

    const newWorkOrder = {
      ...workOrder,
      scopeOfWork: newValue,
    };
    setWorkOrder(newWorkOrder);
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();
    setErrorMessage("");

    setIsBusy(true);

    await workOrderService.createWorkOrder(
      subscription.idSubscription,
      workOrder,
    );

    //setIsBusy(false); // maybe just leave it disabled so it can't ever click to be save as it navigates away anyway and control will fully reload next time

    navigate("/work/work-orders");
  };

  const onCancel = () => {
    navigate("/work/work-orders");
  };

  return (
    <Page
      pageTitle={(isNew ? "Create" : "Edit") + " Work Order"}
      breadcrumbs={[
        { linkName: "Work Orders", linkUlr: "/work/work-orders" },
        { linkName: isNew ? "Create" : "Edit", linkUlr: "" },
      ]}
    >
      <section className="section">
        <div className="row">
          <div className="col-lg">
            <div className="card p-4">
              <form
                className="row g-3"
                onSubmit={onSubmit}
                onKeyDown={formHelper.onEnterKeyDownPreventSubmit}
              >
                <div className="col-12">
                  <label htmlFor="account" className="form-label">
                    Account
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="account"
                    options={accountOptions}
                    onChange={onAccountChanged}
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="location" className="form-label">
                    Location
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="location"
                    options={locationsOptions}
                    value={selectedLocationOption}
                    onChange={onLocationChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="trade" className="form-label">
                    Trade
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="trade"
                    options={tradeOptions}
                    value={selectedTradeOption}
                    onChange={onTradeChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="workOrderType" className="form-label">
                    Work Order Type
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="workOrderType"
                    options={workOrderTypeOptions}
                    value={selectedWorkOrderTypeOption}
                    onChange={onWorkOrderTypeChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="dne" className="form-label">
                    DNE
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="dne"
                    value={workOrder.dneAmount ?? ""}
                    onChange={onDneChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="dueDate" className="form-label">
                    Due Date
                  </label>
                  <div className="calendar-textbox-full-width">
                    <DatePicker
                      className="form-control"
                      placeholderText="Please select"
                      id="dueDate"
                      selected={workOrder.dueDate}
                      onChange={(date: Date) => onDueDateChanged(date)}
                      popperPlacement="left"
                      minDate={new Date()}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <label htmlFor="workOrderPriority" className="form-label">
                    Priority
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="workOrderPriority"
                    options={workOrderPriorityOptions}
                    value={selectedWorkOrderPriotityOption}
                    onChange={onWorkOrderPriorityChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="estimatedLaborHours" className="form-label">
                    Estimated Labor Hours
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="dne"
                    value={workOrder.estimatedLaborHours ?? ""}
                    onChange={onEstimatedLaborHoursChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="tags" className="form-label">
                    Tags (separate tags with ,)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="tags"
                    value={workOrder.tags ?? ""}
                    onChange={onTagsChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="referenceNumber" className="form-label">
                    Reference #
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="referenceNumber"
                    value={workOrder.referenceNumber ?? ""}
                    onChange={onReferenceNumberChanged}
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="referenceNumber" className="form-label">
                    Scope of Work
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Details of the problem or any other instructions"
                    id="referenceNumber"
                    value={workOrder.scopeOfWork ?? ""}
                    onChange={onScopeOfWorkChanged}
                    style={{ height: "100px" }}
                  />
                </div>
                <div className="col-12">
                  <div className="d-flex justify-content-between">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isBusy}
                      >
                        {isNew ? "Create" : "Save"}
                      </button>{" "}
                      <button
                        type="reset"
                        className="btn btn-secondary"
                        onClick={() => onCancel()}
                      >
                        Cancel
                      </button>
                    </div>
                    {/* {!isNew && (
                      <div>
                        <button
                          type="reset"
                          className="btn btn-danger"
                          onClick={() => onDeleteConfirm()}
                          disabled={isBusy}
                        >
                          Delete
                        </button>
                      </div>
                    )} */}
                  </div>
                </div>

                {errorMessage !== "" && (
                  <div className="row text-center">
                    <div className="error-message">{errorMessage}</div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default WorkOrderEdit;
